import type { AstDocument, Block } from '@emailforge/schemas';
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

export type EditorViewport = 'desktop' | 'mobile';
export type EditorColorScheme = 'light' | 'dark';
export type EditorPane = 'canvas' | 'code' | 'variables' | 'preview';

const HISTORY_LIMIT = 100;

type EditorState = {
  templateId: string | null;
  versionId: string | null;
  document: AstDocument;
  selectedBlockId: string | null;
  hoveredBlockId: string | null;
  viewport: EditorViewport;
  colorScheme: EditorColorScheme;
  pane: EditorPane;
  isDirty: boolean;
  isSaving: boolean;
  lastSavedAt: string | null;
  /**
   * Stack of pre-mutation document snapshots. Pushed BEFORE every structural
   * change. Cmd+Z pops the top, swaps it into `document`, and moves the prior
   * `document` to `future`.
   */
  past: AstDocument[];
  future: AstDocument[];
  /**
   * Identity of the most recent edit. While successive mutations carry the
   * same key (e.g. `props:abc123`), we suppress the snapshot push so that
   * typing in an inspector field collapses into one undo step. `null` resets
   * the chain — the next mutation always pushes.
   */
  lastEditKey: string | null;
};

const emptyDocument: AstDocument = {
  version: 1,
  blocks: [],
};

const initialState: EditorState = {
  templateId: null,
  versionId: null,
  document: emptyDocument,
  selectedBlockId: null,
  hoveredBlockId: null,
  viewport: 'desktop',
  colorScheme: 'light',
  pane: 'canvas',
  isDirty: false,
  isSaving: false,
  lastSavedAt: null,
  past: [],
  future: [],
  lastEditKey: null,
};

/**
 * Deep-clone via JSON. AST documents are guaranteed JSON-safe (no Dates,
 * Maps, etc), and we need a real clone — pushing the Immer draft into `past`
 * would store a live reference that mutates with the rest of the reducer.
 */
function snapshot(doc: AstDocument): AstDocument {
  return JSON.parse(JSON.stringify(doc)) as AstDocument;
}

/**
 * Push the current document onto `past` unless this is a continuation of the
 * previous edit (same `editKey`). Caps history at HISTORY_LIMIT and clears
 * the redo stack — once you make a new change after an undo, the redo path
 * is gone (standard editor semantics).
 */
function pushSnapshot(state: EditorState, editKey: string | null) {
  if (editKey !== null && state.lastEditKey === editKey) {
    // Continuous edit — preserve the existing past entry as the rollback target.
    return;
  }
  state.past.push(snapshot(state.document));
  if (state.past.length > HISTORY_LIMIT) state.past.shift();
  state.future = [];
  state.lastEditKey = editKey;
}

const editorSlice = createSlice({
  name: 'editor',
  initialState,
  reducers: {
    loadTemplate(
      state,
      action: PayloadAction<{ templateId: string; versionId: string; document: AstDocument }>,
    ) {
      state.templateId = action.payload.templateId;
      state.versionId = action.payload.versionId;
      state.document = action.payload.document;
      state.isDirty = false;
      // Loading a template is a fresh start — drop any history from a prior
      // template the editor was viewing.
      state.past = [];
      state.future = [];
      state.lastEditKey = null;
    },
    setDocument(state, action: PayloadAction<AstDocument>) {
      pushSnapshot(state, null);
      state.document = action.payload;
      state.isDirty = true;
    },
    addBlock(state, action: PayloadAction<{ block: Block; atIndex?: number }>) {
      pushSnapshot(state, null);
      const index = action.payload.atIndex ?? state.document.blocks.length;
      state.document.blocks.splice(index, 0, action.payload.block);
      state.selectedBlockId = action.payload.block.id;
      state.isDirty = true;
    },
    insertSnippet(state, action: PayloadAction<{ blocks: Block[]; atIndex?: number }>) {
      if (action.payload.blocks.length === 0) return;
      pushSnapshot(state, null);
      const index = action.payload.atIndex ?? state.document.blocks.length;
      state.document.blocks.splice(index, 0, ...action.payload.blocks);
      state.selectedBlockId = action.payload.blocks[0]?.id ?? null;
      state.isDirty = true;
    },
    updateBlockProps(state, action: PayloadAction<{ blockId: string; props: Record<string, unknown> }>) {
      const b = state.document.blocks.find((x) => x.id === action.payload.blockId);
      if (b) {
        // Coalesce successive prop edits on the same block — typing in an
        // inspector field shouldn't produce one undo step per keystroke.
        pushSnapshot(state, `props:${action.payload.blockId}`);
        b.props = { ...(b.props as object), ...action.payload.props } as typeof b.props;
        state.isDirty = true;
      }
    },
    removeBlock(state, action: PayloadAction<string>) {
      pushSnapshot(state, null);
      state.document.blocks = state.document.blocks.filter((b) => b.id !== action.payload);
      if (state.selectedBlockId === action.payload) state.selectedBlockId = null;
      state.isDirty = true;
    },
    moveBlock(state, action: PayloadAction<{ blockId: string; toIndex: number }>) {
      const from = state.document.blocks.findIndex((b) => b.id === action.payload.blockId);
      if (from < 0) return;
      pushSnapshot(state, null);
      const [moved] = state.document.blocks.splice(from, 1);
      if (moved) state.document.blocks.splice(action.payload.toIndex, 0, moved);
      state.isDirty = true;
    },
    /**
     * Insert a new block (typically from the library) into a `columns` block's
     * specific column at `atIndex`. The columns block is identified by id;
     * `columnIndex` is which side (0/1 for 1:1, 0/1/2 for 3-col, etc).
     */
    addBlockToColumn(
      state,
      action: PayloadAction<{
        block: Block;
        columnsBlockId: string;
        columnIndex: number;
        atIndex?: number;
      }>,
    ) {
      const cols = state.document.blocks.find(
        (b) => b.id === action.payload.columnsBlockId && b.type === 'columns',
      );
      if (!cols || cols.type !== 'columns') return;
      pushSnapshot(state, null);
      const col = (cols.children[action.payload.columnIndex] ?? []) as Block[];
      const idx = action.payload.atIndex ?? col.length;
      col.splice(idx, 0, action.payload.block);
      cols.children[action.payload.columnIndex] = col;
      state.selectedBlockId = action.payload.block.id;
      state.isDirty = true;
    },
    /**
     * Move a block already inside a column to a new column/index. Both source
     * and destination are columns of the same `columns` block.
     */
    moveColumnChild(
      state,
      action: PayloadAction<{
        columnsBlockId: string;
        blockId: string;
        toColumnIndex: number;
        toIndex: number;
      }>,
    ) {
      const cols = state.document.blocks.find(
        (b) => b.id === action.payload.columnsBlockId && b.type === 'columns',
      );
      if (!cols || cols.type !== 'columns') return;
      let fromCol = -1;
      let fromIdx = -1;
      for (let i = 0; i < cols.children.length; i += 1) {
        const c = (cols.children[i] ?? []) as Block[];
        const j = c.findIndex((b) => b.id === action.payload.blockId);
        if (j >= 0) {
          fromCol = i;
          fromIdx = j;
          break;
        }
      }
      if (fromCol < 0) return;
      pushSnapshot(state, null);
      const src = cols.children[fromCol] as Block[];
      const [moved] = src.splice(fromIdx, 1);
      if (!moved) return;
      const dst = (cols.children[action.payload.toColumnIndex] ?? []) as Block[];
      // When moving inside the same column to a later index, splice has shifted
      // the target by one. Don't compensate here — let the caller pass the
      // intended visual target index (UI shows ghost dropzones at every slot).
      const adjusted =
        fromCol === action.payload.toColumnIndex && action.payload.toIndex > fromIdx
          ? action.payload.toIndex - 1
          : action.payload.toIndex;
      dst.splice(adjusted, 0, moved);
      cols.children[action.payload.toColumnIndex] = dst;
      state.isDirty = true;
    },
    /**
     * Remove a block from any column it's in. No-op if the id isn't found in
     * any column (e.g. it's a top-level block — use `removeBlock` for that).
     */
    removeColumnChild(
      state,
      action: PayloadAction<{ columnsBlockId: string; blockId: string }>,
    ) {
      const cols = state.document.blocks.find(
        (b) => b.id === action.payload.columnsBlockId && b.type === 'columns',
      );
      if (!cols || cols.type !== 'columns') return;
      pushSnapshot(state, null);
      for (let i = 0; i < cols.children.length; i += 1) {
        const c = (cols.children[i] ?? []) as Block[];
        const j = c.findIndex((b) => b.id === action.payload.blockId);
        if (j >= 0) {
          c.splice(j, 1);
          cols.children[i] = c;
          if (state.selectedBlockId === action.payload.blockId) state.selectedBlockId = null;
          state.isDirty = true;
          return;
        }
      }
    },
    selectBlock(state, action: PayloadAction<string | null>) {
      state.selectedBlockId = action.payload;
    },
    hoverBlock(state, action: PayloadAction<string | null>) {
      state.hoveredBlockId = action.payload;
    },
    setViewport(state, action: PayloadAction<EditorViewport>) {
      state.viewport = action.payload;
    },
    setColorScheme(state, action: PayloadAction<EditorColorScheme>) {
      state.colorScheme = action.payload;
    },
    setPane(state, action: PayloadAction<EditorPane>) {
      state.pane = action.payload;
    },
    saveStarted(state) {
      state.isSaving = true;
    },
    saveCompleted(state) {
      state.isSaving = false;
      state.isDirty = false;
      state.lastSavedAt = new Date().toISOString();
    },
    undo(state) {
      const prev = state.past.pop();
      if (!prev) return;
      state.future.push(snapshot(state.document));
      state.document = prev;
      state.isDirty = true;
      state.lastEditKey = null;
    },
    redo(state) {
      const next = state.future.pop();
      if (!next) return;
      state.past.push(snapshot(state.document));
      state.document = next;
      state.isDirty = true;
      state.lastEditKey = null;
    },
  },
});

export const {
  loadTemplate,
  setDocument,
  addBlock,
  insertSnippet,
  updateBlockProps,
  removeBlock,
  moveBlock,
  addBlockToColumn,
  moveColumnChild,
  removeColumnChild,
  selectBlock,
  hoverBlock,
  setViewport,
  setColorScheme,
  setPane,
  saveStarted,
  saveCompleted,
  undo,
  redo,
} = editorSlice.actions;

export default editorSlice.reducer;
